import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";
import { API } from "../../config";
import Spin from "../Spinner/Spinner";
import styles from "./Listing.module.css";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { RxDragHandleHorizontal } from "react-icons/rx";
import { CSS } from "@dnd-kit/utilities";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const SortableRow = ({ blog, navigate, formatDate, formatTime }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: blog._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRowClick = (e) => {
    if (e.target.closest("[data-drag-handle]")) {
      return;
    }
    navigate(`/blogs/${blog._id}/edit`);
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={styles.blogRow}
      onClick={handleRowClick}
    >
      <td>
        <DragHandle listeners={listeners} attributes={attributes} />
      </td>
      <td>{blog.order || "-"}</td>
      <td style={{ 
        maxWidth: "180px", 
        overflow: "hidden", 
        textOverflow: "ellipsis", 
        whiteSpace: "nowrap"
      }}>
        {blog.heading || "-"}
      </td>
      <td>
        {formatDate(blog.createdAt)} {formatTime(blog.createdAt)}
      </td>
      <td>
        {formatDate(blog.updatedAt)} {formatTime(blog.updatedAt)}
      </td>
      <td>{blog.isActive ? "Active" : "Inactive"}</td>
    </tr>
  );
};

const DragHandle = ({ listeners, attributes }) => {
  return (
    <span
      {...listeners}
      {...attributes}
      data-drag-handle
      style={{
        cursor: "grab",
        padding: "8px",
        display: "inline-block",
        fontSize: "18px",
        userSelect: "none",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <RxDragHandleHorizontal />
    </span>
  );
};

export const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const updateBlogOrder = async (order) => {
    try {
      const response = await fetch(`${API}/Admin/blogs/order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reorderedBlogs: order }),
      });
      const data = await response.json();
      await getAllBlogs();
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const getAllBlogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/Admin/blogs`);
      const data = await response.json();
      const blogsList = data?.blogs?.blogs || [];
      const sortedBlogs = blogsList.sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      setBlogs(sortedBlogs);
      setOriginalOrder(sortedBlogs.map((b) => b._id));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setBlogs((prev) => {
      const oldIndex = prev.findIndex((item) => item._id === active.id);
      const newIndex = prev.findIndex((item) => item._id === over.id);

      const newArray = arrayMove(prev, oldIndex, newIndex);

      newArray.forEach((item, idx) => {
        item.order = idx + 1;
      });

      const newOrderIds = newArray.map((b) => b._id);

      setIsChanged(
        JSON.stringify(newOrderIds) !== JSON.stringify(originalOrder)
      );

      return [...newArray];
    });
  };

  const handleSave = () => {
    const payload = blogs.map((item) => ({
      id: item._id,
      order: item.order,
    }));

    updateBlogOrder(payload);

    setOriginalOrder(blogs.map((b) => b._id));
    setIsChanged(false);
  };

  useEffect(() => {
    getAllBlogs();
  }, []);

  if (loading) return <Spin />;

  return (
    <div className={styles.container}>
      <div className={styles.upper}>
        <Link to="/">
          <span className={styles.swag}>
            Website
            <span className={styles.greater}> &gt; </span>
          </span>
        </Link>
        <span className={styles.req}>Blogs</span>
      </div>
      <h1 className={styles.heading}>Blogs</h1>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          justifyContent: "space-between",
          flexDirection: "row",
        }}
      >
        <button
          onClick={() => navigate("/blogs/new")}
          className={styles.addbutton}
        >
          <AiOutlinePlus /> New Blog
        </button>
        {blogs && blogs.length > 0 && (
          <button
            onClick={handleSave}
            disabled={!isChanged}
            className={styles.addbutton}
            style={{
              width: "fit-content",
            }}
          >
            Save Order
          </button>
        )}
      </div>
      {(!blogs || blogs.length === 0) && (
        <p className={styles.noBlogs}>No blogs found</p>
      )}
      {blogs && blogs.length > 0 && (
        <div className={styles.blogsContainer}>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Order</th>
                <th>Heading</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Status</th>
              </tr>
            </thead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={blogs.map((b) => b._id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {blogs.map((blog) => (
                    <SortableRow
                      key={blog._id}
                      blog={blog}
                      navigate={navigate}
                      formatDate={formatDate}
                      formatTime={formatTime}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>
      )}
    </div>
  );
};
